/**
 * Playlist module for Vuex store
 */
import ApiService from '@/services/ApiService';
import { isValidPlaylistID } from '@/utils/playlistID';

// Minimum time the loading animation stays visible. Just enough to avoid a
// jarring flash on fast loads — NOT a full artificial second on top of every
// page load (it used to be 1000ms, a guaranteed +1s to perceived startup).
const MIN_LOADING_MS = 300;

function waitMinDuration(startTime) {
  const elapsed = Date.now() - startTime;
  const remaining = MIN_LOADING_MS - elapsed;
  return remaining > 0 ? new Promise(r => setTimeout(r, remaining)) : Promise.resolve();
}

// Matches a radio-variant suffix, e.g. "Foo - radio cut" / "Foo - Radio Edit".
const RADIO_VARIANT_SUFFIX = / - radio (cut|edit)$/i;

/**
 * Reorder an already-sorted list so each "<title> - radio cut|edit" variant sits
 * immediately before its original "<title>". A variant is only moved when its
 * original is present in the list; everything else keeps its order. Matching is
 * case-insensitive on the suffix. Other suffixes (e.g. "- remix") are left alone.
 *
 * Used by the chronological sort, where a variant shares its original's date but
 * the ID tie-break would otherwise scatter them apart.
 */
function reorderRadioVariantsBeforeOriginals(sorted) {
  // Group variants by their base (original) title, preserving the sorted order.
  const variantsByBase = new Map();
  for (const recording of sorted) {
    const match = recording.name.match(RADIO_VARIANT_SUFFIX);
    if (!match) continue;
    const base = recording.name.slice(0, match.index);
    if (!variantsByBase.has(base)) variantsByBase.set(base, []);
    variantsByBase.get(base).push(recording);
  }
  if (variantsByBase.size === 0) return sorted;

  // Only relocate variants whose original is actually present.
  const presentNames = new Set(sorted.map(r => r.name));
  const relocated = new Set();
  for (const [base, variants] of variantsByBase) {
    if (presentNames.has(base)) variants.forEach(v => relocated.add(v));
  }
  if (relocated.size === 0) return sorted;

  const result = [];
  for (const recording of sorted) {
    if (relocated.has(recording)) continue; // re-inserted just before its original
    const variants = variantsByBase.get(recording.name);
    if (variants) {
      // `recording` is an original — emit its variants first, then itself.
      variants.forEach(v => { if (relocated.has(v)) result.push(v); });
      variantsByBase.delete(recording.name); // place only before the first match
    }
    result.push(recording);
  }
  return result;
}

export default {
  namespaced: true,
  
  state: {
    recordings: [],
    isLoading: true,
    loadError: false,
    playlistID: null,
    playlistURL: null,
    newPlaylistURL: null,
    isShowingPlaylistCreatedDialog: false,
    isDefaultPlaylist: false,
    // The ID of the last playlist link that turned out not to exist (404). Set when
    // loadPlaylist falls back to the home playlist; MusicPlayer watches it to move
    // the URL back to "/".
    notFoundPlaylistID: null,
  },

  getters: {
    recordings: state => state.recordings,
    isLoading: state => state.isLoading,
    loadError: state => state.loadError,
    playlistID: state => state.playlistID,
    playlistURL: state => state.playlistURL,
    newPlaylistURL: state => state.newPlaylistURL,
    isShowingPlaylistCreatedDialog: state => state.isShowingPlaylistCreatedDialog,
    isDefaultPlaylist: state => state.isDefaultPlaylist,
    notFoundPlaylistID: state => state.notFoundPlaylistID,
    
    sortedRecordings: (state, getters, rootState) => {
      // Deduplicate recordings based on name, date, and duration
      // Keep only the first occurrence (lowest ID) of each unique combination
      const seen = new Map();
      const deduplicatedRecordings = [];

      for (const recording of state.recordings) {
        const key = `${recording.name}|${recording.date}|${recording.duration}`;
        if (!seen.has(key)) {
          seen.set(key, true);
          deduplicatedRecordings.push(recording);
        }
      }

      const recordingsArray = [...deduplicatedRecordings];

      // Sort based on the user's explicit choice first.
      if (rootState.ui.sortOrder === 'alphabetically') {
        recordingsArray.sort((a, b) => a.name.localeCompare(b.name));
        // Alphabetical view is left exactly alphabetical (no variant grouping).
        return recordingsArray;
      }

      // A genuine album = EVERY track carries a numeric `misc` track number.
      // IMPORTANT: real catalog rows (the master playlist AND user-created
      // playlists) come back with misc=null, and null must NOT count as a
      // track number. The old check here (`misc !== undefined`) routed
      // all-null playlists into the album branch, where parseInt(null)=NaN
      // turned the comparator into a no-op — the list silently kept the
      // backend's arbitrary row order, and the year labels (rendered from
      // list order) came out wrong on shared playlists.
      const isGenuineAlbum = recordingsArray.length > 0 &&
        recordingsArray.every(r => r.misc != null && !Number.isNaN(parseInt(r.misc, 10)));

      if (isGenuineAlbum) {
        // A real album keeps its explicit track-number order (no variant grouping).
        recordingsArray.sort((a, b) => parseInt(a.misc, 10) - parseInt(b.misc, 10));
        return recordingsArray;
      }

      // Chronological sort (default): newest year first; within a year oldest
      // date first; ID descending breaks exact-date ties. A missing or
      // malformed date sorts to the very bottom (year 0) instead of throwing.
      const yearOf = recording => {
        const year = parseInt(String(recording.date || '').slice(0, 4), 10);
        return Number.isNaN(year) ? 0 : year;
      };

      recordingsArray.sort((a, b) => {
        const yearA = yearOf(a);
        const yearB = yearOf(b);

        if (yearA !== yearB) {
          return yearB - yearA; // Newest year first
        }

        // Within same year, oldest date first
        const dateComparison = String(a.date || '').localeCompare(String(b.date || ''));
        if (dateComparison !== 0) {
          return dateComparison;
        }

        // If dates are equal, sort by ID descending (highest ID first)
        return b.id - a.id;
      });

      // In the chronological/default view, keep each "- radio cut|edit" version
      // directly in front of its original.
      return reorderRadioVariantsBeforeOriginals(recordingsArray);
    }
  },
  
  mutations: {
    SET_RECORDINGS(state, recordings) {
      state.recordings = recordings;
    },
    SET_IS_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_LOAD_ERROR(state, loadError) {
      state.loadError = loadError;
    },
    SET_PLAYLIST_ID(state, playlistID) {
      state.playlistID = playlistID;
    },
    SET_PLAYLIST_URL(state, playlistURL) {
      state.playlistURL = playlistURL;
    },
    SET_NEW_PLAYLIST_URL(state, newPlaylistURL) {
      state.newPlaylistURL = newPlaylistURL;
    },
    SET_IS_SHOWING_PLAYLIST_CREATED_DIALOG(state, isShowing) {
      state.isShowingPlaylistCreatedDialog = isShowing;
    },
    SET_IS_DEFAULT_PLAYLIST(state, isDefault) {
      state.isDefaultPlaylist = isDefault;
    },
    SET_NOT_FOUND_PLAYLIST_ID(state, playlistID) {
      state.notFoundPlaylistID = playlistID;
    },
    SET_NUMBER_OF_LISTENS(state, { id, updatedNumberOfListens }) {
      for (const recording of state.recordings) {
        if (recording.id === id) {
          recording.numberOfListens = updatedNumberOfListens;
          break;
        }
      }
    },
    REMOVE_RECORDING(state, id) {
      state.recordings = state.recordings.filter(r => r.id !== id);
    },
  },
  
  actions: {
    async loadPlaylist({ commit, dispatch, rootGetters }, playlistID) {
      // Reset loading state
      const startTime = Date.now();
      commit('SET_IS_LOADING', true);
      commit('SET_LOAD_ERROR', false);
      commit('SET_IS_DEFAULT_PLAYLIST', false);

      // Validate playlist ID: a 7-char share ID or a vanity alias (see utils/playlistID.js).
      // Anything else is "no playlist" → the public playlist, as before.
      if (!isValidPlaylistID(playlistID)) {
        await dispatch('loadDefaultPlaylist');
        return;
      }

      try {
        const { recordings, playlistURL } = await ApiService.loadPlaylist(playlistID);
        commit('SET_RECORDINGS', recordings);
        commit('SET_PLAYLIST_URL', playlistURL);
      } catch (error) {
        const status = error && error.response && error.response.status;

        // A link that doesn't exist (mistyped, or the playlist was deleted): the API
        // answers 404. Instead of an error screen, fall back to what "/" would show —
        // the master playlist when signed in, the public playlist otherwise — say so,
        // and let MusicPlayer move the URL back to "/" (it watches notFoundPlaylistID).
        if (status === 404 && playlistID !== 'default') {
          console.warn('Playlist not found, falling back to the home playlist:', playlistID);
          commit('SET_NOT_FOUND_PLAYLIST_ID', playlistID);
          dispatch('ui/showSnackbar', { text: 'Playlist not found', timeout: 5000 }, { root: true });
          if (rootGetters['auth/isLoggedIn']) {
            await dispatch('loadMasterPlaylist');
          } else {
            await dispatch('loadDefaultPlaylist');
          }
          return;
        }

        console.error('Failed to load playlist:', error);
        commit('SET_LOAD_ERROR', true);
      } finally {
        await waitMinDuration(startTime);
        commit('SET_IS_LOADING', false);
      }
    },

    async loadDefaultPlaylist({ commit, dispatch }) {
      await dispatch('loadPlaylist', 'default');
      commit('SET_PLAYLIST_ID', null);
      commit('SET_PLAYLIST_URL', null);
      commit('SET_IS_DEFAULT_PLAYLIST', true);
    },
    
    async loadMasterPlaylist({ commit, rootGetters }) {
      // Reset loading state
      const startTime = Date.now();
      commit('SET_IS_LOADING', true);
      commit('SET_LOAD_ERROR', false);
      commit('SET_IS_DEFAULT_PLAYLIST', false);

      const userID = rootGetters['auth/userID'];
      if (!userID) {
        console.error('Cannot load master playlist: no user ID');
        await waitMinDuration(startTime);
        commit('SET_IS_LOADING', false);
        return;
      }

      try {
        const { recordings, isSuperAdmin } = await ApiService.loadMasterPlaylist(userID);
        commit('SET_RECORDINGS', recordings);
        commit('SET_PLAYLIST_ID', null);
        commit('SET_PLAYLIST_URL', null);

        if (isSuperAdmin) {
          commit('auth/SET_IS_SUPER_ADMIN', true, { root: true });
        }
      } catch (error) {
        console.error('Failed to load master playlist:', error);
        commit('SET_LOAD_ERROR', true);
      } finally {
        await waitMinDuration(startTime);
        commit('SET_IS_LOADING', false);
      }
    },

    async createPlaylist({ commit, state, rootGetters }, selectedTracksIDs) {
      const userID = rootGetters['auth/userID'];
      
      if (!state.recordings || state.recordings.length === 0 || 
          !selectedTracksIDs || selectedTracksIDs.length === 0 || 
          !userID || userID === '') {
        console.error('Cannot create playlist: invalid parameters');
        return;
      }
      
      try {
        const { playlistURL } = await ApiService.createPlaylist(userID, selectedTracksIDs);
        commit('SET_NEW_PLAYLIST_URL', playlistURL);
        commit('SET_IS_SHOWING_PLAYLIST_CREATED_DIALOG', true);
        return playlistURL;
      } catch (error) {
        console.error('Failed to create playlist:', error);
        throw error;
      }
    },

    resetPlaylist({ commit }) {
      commit('SET_PLAYLIST_ID', null);
      commit('SET_PLAYLIST_URL', null);
      commit('SET_IS_SHOWING_PLAYLIST_CREATED_DIALOG', false);
    },
    
    async deleteRecording({ commit, rootGetters, dispatch }, recordingID) {
      const userID = rootGetters['auth/userID'];
      if (!userID || !recordingID) return;

      try {
        await ApiService.deleteRecording(recordingID, userID);
        commit('REMOVE_RECORDING', recordingID);
        dispatch('ui/showSnackbar', {
          text: 'Moved to trash — recoverable for 30 days',
          timeout: 5000,
        }, { root: true });
      } catch (error) {
        console.error('Failed to delete recording:', error);
        dispatch('ui/showSnackbar', 'Failed to delete recording', { root: true });
        throw error;
      }
    },

    async incrementNumberOfListens({ commit }, recordingID) {
      if (!recordingID) return;
      
      try {
        const { updatedNumberOfListens } = await ApiService.incrementNumberOfListens(recordingID);
        commit('SET_NUMBER_OF_LISTENS', { 
          id: recordingID, 
          updatedNumberOfListens 
        });
      } catch (error) {
        console.error('Failed to increment listens:', error);
      }
    }
  }
};